import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import {
  DESKTOP_MEDIA_QUERY,
  getSellerVideoSources,
  isDesktopViewport,
  prefersReducedMotion,
  subscribeToMediaQuery,
} from '../../../../util/heroVideo';

import css from './ClickToPlayVideo.module.css';

/**
 * Click-to-play video for the Become a Seller hero.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className
 * @param {string?} props.rootClassName
 * @returns {JSX.Element}
 */
export const ClickToPlayVideo = props => {
  const { className, rootClassName } = props;
  const intl = useIntl();
  const videoRef = useRef(null);
  const [isDesktop, setIsDesktop] = useState(true);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    setIsDesktop(isDesktopViewport());
    return subscribeToMediaQuery(DESKTOP_MEDIA_QUERY, setIsDesktop);
  }, []);

  const sources = getSellerVideoSources(isDesktop);

  useEffect(() => {
    const video = videoRef.current;
    if (!hasStarted || !video) {
      return;
    }
    video.load();
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  }, [hasStarted, sources.mp4]);

  useEffect(() => {
    if (!hasStarted) {
      return;
    }

    const node = videoRef.current;
    if (!node || typeof node.scrollIntoView !== 'function') {
      return;
    }

    const scrollVideoIntoView = () => {
      const rect = node.getBoundingClientRect();
      const fitsInViewport = rect.height <= window.innerHeight;
      node.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: fitsInViewport ? 'center' : 'start',
        inline: 'nearest',
      });
    };

    const frameId = window.requestAnimationFrame(scrollVideoIntoView);
    return () => window.cancelAnimationFrame(frameId);
  }, [hasStarted]);

  const handlePlayClick = () => {
    setHasStarted(true);
  };

  const label = intl.formatMessage({ id: 'ClickToPlayVideo.label' });

  return (
    <div className={classNames(rootClassName || css.root, className)}>
      <div className={css.frame}>
        <video
          ref={videoRef}
          className={css.video}
          src={sources.mp4}
          poster={sources.poster}
          preload="none"
          playsInline
          controls={hasStarted}
          onPlay={() => setHasStarted(true)}
          aria-label={label}
        />
        {hasStarted ? null : (
          <button type="button" className={css.playButton} onClick={handlePlayClick}>
            <span className={css.playIcon} aria-hidden="true" />
            <FormattedMessage id="ClickToPlayVideo.play" />
          </button>
        )}
      </div>
    </div>
  );
};

ClickToPlayVideo.displayName = 'ClickToPlayVideo';
